import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Settings, Wrench } from 'lucide-react';

const Dies = () => {
  const { hasRole } = useAuth();
  const [dies, setDies] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('dies');
  const [dieDialogOpen, setDieDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [dieFormData, setDieFormData] = useState({
    code: '',
    name: '',
    ownership: 'OWNED',
    vendor: ''
  });

  const [eventFormData, setEventFormData] = useState({
    die_id: '',
    event_type: 'POLISH',
    event_datetime: new Date().toISOString().slice(0, 16),
    cost: '',
    notes: '',
    vendor: ''
  });

  const canManage = hasRole(['ADMIN', 'OWNER', 'MUNIM']);

  useEffect(() => {
    fetchDies();
    fetchEvents();
  }, []);

  const fetchDies = async () => {
    try {
      const response = await api.dies.list();
      setDies(response.data);
    } catch (error) {
      toast.error('Failed to fetch dies');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.dieEvents.list();
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const handleDieSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.dies.create({
        ...dieFormData,
        vendor: dieFormData.vendor || null
      });
      toast.success('Die created successfully');
      setDieDialogOpen(false);
      resetDieForm();
      fetchDies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create die');
    } finally {
      setLoading(false);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.dieEvents.create({
        ...eventFormData,
        event_datetime: new Date(eventFormData.event_datetime).toISOString(),
        cost: eventFormData.cost ? parseFloat(eventFormData.cost) : null,
        notes: eventFormData.notes || null,
        vendor: eventFormData.vendor || null
      });
      toast.success('Event recorded successfully');
      setEventDialogOpen(false);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record event');
    } finally {
      setLoading(false);
    }
  };

  const resetDieForm = () => {
    setDieFormData({
      code: '',
      name: '',
      ownership: 'OWNED',
      vendor: ''
    });
  };

  const resetEventForm = () => {
    setEventFormData({
      die_id: '',
      event_type: 'POLISH',
      event_datetime: new Date().toISOString().slice(0, 16),
      cost: '',
      notes: '',
      vendor: ''
    });
  };

  return (
    <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="dies-page">
      <div className="flex items-center justify-between border-b border-[#333333] pb-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">Die Management</h1>
          <p className="text-slate-400 font-sans text-xs tracking-wider uppercase mt-1">Manage dies and maintenance events</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Sub-nav Tabs */}
        <div className="flex gap-1 border-b border-[#333333] pb-px">
          <button
            type="button"
            onClick={() => setActiveTab('dies')}
            className={`h-10 px-6 font-mono text-xs uppercase tracking-wider font-bold rounded-none border-b-2 transition-colors ${
              activeTab === 'dies'
                ? 'bg-[#FF6B00] text-white border-[#FF6B00]'
                : 'bg-[#1E1E1E] border-transparent text-slate-400 hover:text-white hover:bg-[#2A2A2A]'
            }`}
          >
            Dies Inventory
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('events')}
            className={`h-10 px-6 font-mono text-xs uppercase tracking-wider font-bold rounded-none border-b-2 transition-colors ${
              activeTab === 'events'
                ? 'bg-[#FF6B00] text-white border-[#FF6B00]'
                : 'bg-[#1E1E1E] border-transparent text-slate-400 hover:text-white hover:bg-[#2A2A2A]'
            }`}
          >
            Maintenance History
          </button>
        </div>

        {activeTab === 'dies' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              {canManage && (
                <Dialog open={dieDialogOpen} onOpenChange={setDieDialogOpen}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      onClick={resetDieForm}
                      className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="create-die-button"
                    >
                      <Plus className="h-4 w-4" />
                      Add Die
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
                    <DialogHeader className="border-b border-[#333333] pb-3">
                      <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Add New Die</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleDieSubmit} className="space-y-4 pt-4">
                      <div>
                        <label htmlFor="code" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Die Code *</label>
                        <input
                          id="code"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={dieFormData.code}
                          onChange={(e) => setDieFormData({ ...dieFormData, code: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="name" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Name *</label>
                        <input
                          id="name"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={dieFormData.name}
                          onChange={(e) => setDieFormData({ ...dieFormData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Ownership *</label>
                        <select
                          className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                          value={dieFormData.ownership}
                          onChange={(e) => setDieFormData({ ...dieFormData, ownership: e.target.value })}
                        >
                          <option value="OWNED">Owned</option>
                          <option value="BORROWED">Borrowed</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="vendor" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Vendor (Optional)</label>
                        <input
                          id="vendor"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={dieFormData.vendor}
                          onChange={(e) => setDieFormData({ ...dieFormData, vendor: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
                        <button
                          type="button"
                          className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                          onClick={() => setDieDialogOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                          data-testid="submit-die"
                        >
                          {loading ? 'Creating...' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="bg-[#1E1E1E] border border-[#333333] rounded-none">
              <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center gap-2">
                <Settings className="h-4 w-4 text-[#FF6B00]" />
                <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-white">Die Inventory</h2>
              </div>
              <div className="p-4">
                {dies.length === 0 ? (
                  <div className="text-center py-8 font-mono text-xs text-slate-400">NO DIES REGISTERED YET</div>
                ) : (
                  <div className="divide-y divide-[#333333]/30">
                    {dies.map(die => (
                      <div key={die.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="font-mono text-sm font-bold text-white tracking-wider">{die.code}</span>
                            <span className="font-mono text-[9px] bg-[#2A2A2A] border border-[#333333] px-2 py-0.5 text-slate-300">
                              {die.ownership}
                            </span>
                            {die.is_active ? (
                              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#10B981]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981] inline-block" /> ACTIVE
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#6B7280]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280] shadow-[0_0_6px_#6B7280] inline-block" /> INACTIVE
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-slate-300 font-sans">{die.name}</p>
                          {die.vendor && (
                            <p className="text-[10px] font-mono text-slate-400 mt-1 uppercase">Vendor: {die.vendor}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              {canManage && (
                <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      onClick={resetEventForm}
                      className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="create-event-button"
                    >
                      <Plus className="h-4 w-4" />
                      Record Event
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
                    <DialogHeader className="border-b border-[#333333] pb-3">
                      <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Record Die Event</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEventSubmit} className="space-y-4 pt-4">
                      <div>
                        <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Die *</label>
                        <select
                          className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                          value={eventFormData.die_id}
                          onChange={(e) => setEventFormData({ ...eventFormData, die_id: e.target.value })}
                          required
                          data-testid="event-die-select"
                        >
                          <option value="">Select die</option>
                          {dies.filter(d => d.is_active).map(die => (
                            <option key={die.id} value={die.id}>{die.code} - {die.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Event Type *</label>
                        <select
                          className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                          value={eventFormData.event_type}
                          onChange={(e) => setEventFormData({ ...eventFormData, event_type: e.target.value })}
                        >
                          <option value="POLISH">Polish</option>
                          <option value="REPAIR">Repair</option>
                          <option value="BEARING_CHANGE">Bearing Change</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="event_datetime" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">DateTime *</label>
                        <input
                          id="event_datetime"
                          type="datetime-local"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                          value={eventFormData.event_datetime}
                          onChange={(e) => setEventFormData({ ...eventFormData, event_datetime: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="cost" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Cost (Optional)</label>
                        <input
                          id="cost"
                          type="number"
                          step="0.01"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={eventFormData.cost}
                          onChange={(e) => setEventFormData({ ...eventFormData, cost: e.target.value })}
                        />
                      </div>
                      <div>
                        <label htmlFor="event_notes" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Notes</label>
                        <textarea
                          id="event_notes"
                          className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                          value={eventFormData.notes}
                          onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
                        />
                      </div>
                      <div>
                        <label htmlFor="event_vendor" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Vendor (Optional)</label>
                        <input
                          id="event_vendor"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={eventFormData.vendor}
                          onChange={(e) => setEventFormData({ ...eventFormData, vendor: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
                        <button
                          type="button"
                          className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                          onClick={() => setEventDialogOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                          data-testid="submit-event"
                        >
                          {loading ? 'Recording...' : 'Record'}
                        </button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="bg-[#1E1E1E] border border-[#333333] rounded-none">
              <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#FF6B00]" />
                <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-white">Maintenance History</h2>
              </div>
              <div className="p-4">
                {events.length === 0 ? (
                  <div className="text-center py-8 font-mono text-xs text-slate-400">NO MAINTENANCE HISTORY YET</div>
                ) : (
                  <div className="divide-y divide-[#333333]/30">
                    {events.map(event => {
                      const die = dies.find(d => d.id === event.die_id);
                      return (
                        <div key={event.id} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2.5 mb-1.5">
                              <span className="font-mono text-[9px] bg-[#2A2A2A] border border-[#333333] px-2 py-0.5 text-slate-300 uppercase tracking-wider">
                                {event.event_type.replace('_', ' ')}
                              </span>
                              {die && <span className="font-mono text-xs font-bold text-white tracking-wider">{die.code}</span>}
                            </div>
                            <p className="text-[10px] font-mono text-slate-400">
                              {new Date(event.event_datetime).toLocaleString()}
                            </p>
                            {event.cost && (
                              <p className="text-xs font-mono font-bold text-[#FF6B00] mt-1">₹{event.cost}</p>
                            )}
                            {event.notes && (
                              <p className="text-xs text-slate-300 mt-1 font-sans bg-[#2A2A2A]/40 p-2 border-l border-[#333333]">{event.notes}</p>
                            )}
                            {event.vendor && (
                              <p className="text-[10px] font-mono text-slate-400 mt-1 uppercase">Vendor: {event.vendor}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dies;
